import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  Grid,
  LinearProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Card,
  CardContent,
  TextField,
  Paper,
  Stack,
  Tooltip,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery,
  InputBase,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  AccessTime as AccessTimeIcon,
  Flag as FlagIcon,
  Label as LabelIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";

// RTK Query hooks
import {
  useGetTaskQuery,
  useUpdateTaskMutation,
  useAddCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
} from "../../../services/taskApi";

// Socket service
import socketService from "../../../services/socketService";

// Redux store and taskApi
import { store } from "../../../store/store";
import { taskApi } from "../../../services/taskApi";

const TaskDetail = ({
  open,
  taskId,
  onClose,
  onEdit,
  onDelete,
  onStatusUpdate,
}) => {
  // Mobile responsiveness hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // RTK Query hooks
  const {
    data: taskData,
    isLoading: taskLoading,
    error: taskError,
  } = useGetTaskQuery(taskId, {
    skip: !taskId || !open,
    refetchOnMountOrArgChange: true,
  });
  const [updateTask] = useUpdateTaskMutation();
  const [addComment] = useAddCommentMutation();
  const [updateComment] = useUpdateCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();

  const [taskMenuAnchorEl, setTaskMenuAnchorEl] = useState(null);
  const [commentMenuAnchorEl, setCommentMenuAnchorEl] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentModal, setCommentModal] = useState({
    open: false,
    mode: "edit", // 'edit' or 'delete'
    comment: null,
  });
  const [editCommentText, setEditCommentText] = useState("");
  const [customProgress, setCustomProgress] = useState(0);

  // Extract task from RTK Query data
  const task = taskData?.data?.task;

  // Update customProgress when task data changes
  React.useEffect(() => {
    if (task?.progress !== undefined) {
      setCustomProgress(task.progress);
    }
  }, [task?.progress]);

  // Socket event listeners for real-time comment updates
  useEffect(() => {
    if (!open || !taskId) return;

    const handleCommentAdded = (data) => {
      console.log("📨 TaskDetail received task:comment-added event:", data);
      if (data.taskId === taskId) {
        // Invalidate task cache to trigger refetch
        console.log("🔄 Invalidating task cache for new comment");
        store.dispatch(
          taskApi.util.invalidateTags([{ type: "Task", id: taskId }])
        );
      }
    };

    const handleCommentUpdated = (data) => {
      console.log("📨 TaskDetail received task:comment-updated event:", data);
      if (data.taskId === taskId) {
        console.log("🔄 Invalidating task cache for updated comment");
        store.dispatch(
          taskApi.util.invalidateTags([{ type: "Task", id: taskId }])
        );
      }
    };

    const handleCommentDeleted = (data) => {
      console.log("📨 TaskDetail received task:comment-deleted event:", data);
      if (data.taskId === taskId) {
        console.log("🔄 Invalidating task cache for deleted comment");
        store.dispatch(
          taskApi.util.invalidateTags([{ type: "Task", id: taskId }])
        );
      }
    };

    // Add event listeners
    socketService.socket?.on("task:comment-added", handleCommentAdded);
    socketService.socket?.on("task:comment-updated", handleCommentUpdated);
    socketService.socket?.on("task:comment-deleted", handleCommentDeleted);

    // Cleanup function
    return () => {
      socketService.socket?.off("task:comment-added", handleCommentAdded);
      socketService.socket?.off("task:comment-updated", handleCommentUpdated);
      socketService.socket?.off("task:comment-deleted", handleCommentDeleted);
    };
  }, [open, taskId]);

  if (!open) return null;

  if (taskLoading) {
    return (
      <Dialog open={open} maxWidth="lg" fullWidth>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 8,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #1976d2",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                mx: "auto",
                mb: 2,
              }}
            />
            <Typography>Loading task details...</Typography>
          </Box>
        </Box>
      </Dialog>
    );
  }

  if (taskError || !task) {
    return (
      <Dialog open={open} maxWidth="lg" fullWidth>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error" variant="h6" gutterBottom>
            Failed to load task
          </Typography>
          <Typography color="text.secondary">
            {taskError?.data?.message || "Task not found"}
          </Typography>
          <Button onClick={onClose} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Dialog>
    );
  }

  const handleTaskMenuOpen = (event) => {
    setTaskMenuAnchorEl(event.currentTarget);
  };

  const handleTaskMenuClose = () => {
    setTaskMenuAnchorEl(null);
  };

  const handleCommentMenuOpen = (event) => {
    setCommentMenuAnchorEl(event.currentTarget);
  };

  const handleCommentMenuClose = () => {
    setCommentMenuAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      const updates = { status: newStatus };

      // Auto-set progress based on status changes
      switch (newStatus) {
        case "pending":
          updates.progress = 0;
          break;
        case "in_progress":
          // If reopening from completed/cancelled, set to 25%, otherwise keep current progress
          if (task.status === "completed" || task.status === "cancelled") {
            updates.progress = 25;
          }
          break;
        case "on_hold":
          // Keep current progress when putting on hold
          break;
        case "completed":
          updates.progress = 100;
          break;
        case "cancelled":
          updates.progress = 0;
          break;
        default:
          break;
      }

      await updateTask({ taskId: task._id, updates }).unwrap();
      onStatusUpdate(task, newStatus);
    } catch (error) {
      console.error("Failed to update status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;

    try {
      await addComment({
        taskId: task._id,
        comment: commentText.trim(),
      }).unwrap();
      setCommentText("");
      showNotification("Comment added successfully", "success");

      // Auto-scroll to bottom after adding comment
      setTimeout(() => {
        const commentsContainer = document.querySelector(
          "[data-comments-container]"
        );
        if (commentsContainer) {
          commentsContainer.scrollTop = commentsContainer.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error("Failed to add comment:", error);
      showNotification("Failed to add comment", "error");
    }
  };

  const handleOpenCommentModal = (mode, comment) => {
    setCommentModal({
      open: true,
      mode,
      comment,
    });
    if (mode === "edit" && comment) {
      setEditCommentText(comment.message);
    }
  };

  const handleCloseCommentModal = () => {
    setCommentModal({
      open: false,
      mode: "edit",
      comment: null,
    });
    setEditCommentText("");
  };

  const handleEditComment = async (commentId, message) => {
    if (!message.trim()) return;

    try {
      setIsSubmitting(true);
      await updateComment({
        taskId: task._id,
        commentId,
        message: message.trim(),
      }).unwrap();
      handleCloseCommentModal();
      showNotification("Comment updated successfully", "success");
    } catch (error) {
      console.error("Failed to update comment:", error);
      showNotification("Failed to update comment", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setIsSubmitting(true);
      await deleteComment({
        taskId: task._id,
        commentId,
      }).unwrap();
      handleCloseCommentModal();
      showNotification("Comment deleted successfully", "success");
    } catch (error) {
      console.error("Failed to delete comment:", error);
      showNotification("Failed to delete comment", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showNotification = (message, severity = "success") => {
    setNotification({ message, severity });
    setTimeout(() => setNotification(null), 3000);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "#d32f2f";
      case "high":
        return "#f57c00";
      case "medium":
        return "#1976d2";
      case "low":
        return "#388e3c";
      default:
        return "#757575";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "#4caf50";
      case "in_progress":
        return "#2196f3";
      case "on_hold":
        return "#ff9800";
      case "pending":
        return "#9e9e9e";
      case "cancelled":
        return "#f44336";
      default:
        return "#757575";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleProgressUpdate = async (newProgress) => {
    try {
      setLoading(true);
      await updateTask({
        taskId: task._id,
        updates: { progress: newProgress },
      }).unwrap();
      showNotification(`Progress updated to ${newProgress}%`, "success");
    } catch (error) {
      console.error("Failed to update progress:", error);
      showNotification("Failed to update progress", "error");
    } finally {
      setLoading(false);
    }
  };

  const getStatusActions = () => {
    const actions = [];
    if (task.status === "pending") {
      actions.push({
        label: "Start Task",
        status: "in_progress",
        icon: PlayArrowIcon,
      });
    } else if (task.status === "in_progress") {
      actions.push({
        label: "Mark Complete",
        status: "completed",
        icon: CheckCircleIcon,
      });
      actions.push({
        label: "Put On Hold",
        status: "on_hold",
        icon: PauseIcon,
      });
    } else if (task.status === "on_hold") {
      actions.push({
        label: "Resume Task",
        status: "in_progress",
        icon: PlayArrowIcon,
      });
    } else if (task.status === "completed") {
      actions.push({
        label: "Reopen Task",
        status: "in_progress",
        icon: PlayArrowIcon,
      });
    }
    return actions;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={!isMobile}
      fullScreen={isMobile}
      TransitionComponent={Fade}
      transitionDuration={250}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          maxHeight: isMobile ? "100vh" : "95vh",
          maxWidth: isMobile ? "100%" : "lg",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #020617 0%, #020617 40%, #0f172a 100%)"
              : "linear-gradient(135deg, #f9fafb 0%, #ffffff 40%, #eff6ff 100%)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: isMobile ? 2.5 : 3.5,
          pt: isMobile ? 2 : 3,
          pb: isMobile ? 2 : 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `radial-gradient(circle at top left, ${getStatusColor(task.status)}22 0, transparent 55%)`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  pr: 1,
                  background: `linear-gradient(135deg, ${getStatusColor(task.status)} 0%, ${getStatusColor(task.status)}cc 60%, #111827 100%)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: isMobile ? "1.45rem" : "2rem",
                }}
              >
                {task.title}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title="More actions">
                  <IconButton
                    onClick={handleTaskMenuOpen}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.9)",
                      border: "1px solid rgba(148,163,184,0.35)",
                      boxShadow: "0 8px 20px rgba(15,23,42,0.12)",
                      "&:hover": {
                        backgroundColor: "rgba(248,250,252,1)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.18s ease-out",
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Close">
                  <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                      ml: 0.5,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(15, 23, 42, 0.06)",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Chips */}
            <Stack
              direction="row"
              spacing={1}
              mt={1.5}
              sx={{ flexWrap: "wrap", gap: 0.75 }}
            >
              <Chip
                icon={<FlagIcon />}
                size="small"
                label={task.priority.toUpperCase()}
                sx={{
                  backgroundColor: `${getPriorityColor(task.priority)}12`,
                  color: getPriorityColor(task.priority),
                  fontWeight: 600,
                  borderRadius: 999,
                  border: `1px solid ${getPriorityColor(task.priority)}33`,
                  fontSize: "0.7rem",
                  "& .MuiChip-icon": { fontSize: 16 },
                }}
              />
              <Chip
                icon={<TimelineIcon />}
                size="small"
                label={task.status.replace("_", " ").toUpperCase()}
                sx={{
                  backgroundColor: `${getStatusColor(task.status)}14`,
                  color: getStatusColor(task.status),
                  fontWeight: 600,
                  borderRadius: 999,
                  border: `1px solid ${getStatusColor(task.status)}33`,
                  fontSize: "0.7rem",
                  "& .MuiChip-icon": { fontSize: 16 },
                }}
              />
              {task.category && (
                <Chip
                  icon={<LabelIcon />}
                  size="small"
                  label={task.category}
                  variant="outlined"
                  sx={{
                    borderRadius: 999,
                    borderColor: "primary.main",
                    color: "primary.main",
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    "& .MuiChip-icon": { fontSize: 16 },
                  }}
                />
              )}
              {task.isOverdue && (
                <Chip
                  icon={<AccessTimeIcon />}
                  size="small"
                  label="OVERDUE"
                  sx={{
                    borderRadius: 999,
                    backgroundColor: "error.main",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    "@keyframes pulse": {
                      "0%": { transform: "scale(1)", opacity: 1 },
                      "50%": { transform: "scale(1.03)", opacity: 0.85 },
                      "100%": { transform: "scale(1)", opacity: 1 },
                    },
                    animation: "pulse 1.8s ease-in-out infinite",
                  }}
                />
              )}
            </Stack>

            {/* Header Meta Row */}
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={1.5}
              mt={2}
              sx={{
                alignItems: isMobile ? "flex-start" : "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.6,
                    borderRadius: 999,
                    backgroundColor: "rgba(15,23,42,0.02)",
                    border: "1px solid rgba(148,163,184,0.4)",
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 16, opacity: 0.75 }} />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {formatDate(task.startDate)} → {formatDate(task.dueDate)}
                  </Typography>
                </Box>
                {task.estimatedHours > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.25,
                      py: 0.6,
                      borderRadius: 999,
                      backgroundColor: "rgba(56,189,248,0.08)",
                      border: "1px solid rgba(56,189,248,0.4)",
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      {task.estimatedHours}h estimate
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Progress preview */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  minWidth: isMobile ? "100%" : 220,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <LinearProgress
                    variant="determinate"
                    value={task.progress}
                    sx={{
                      height: 6,
                      borderRadius: 999,
                      backgroundColor: "rgba(148, 163, 184, 0.25)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${getStatusColor(task.status)} 0%, ${getStatusColor(task.status)}dd 60%, #111827 100%)`,
                      },
                    }}
                  />
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: getStatusColor(task.status),
                    }}
                  >
                    {task.progress}%
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                  >
                    Complete
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: isMobile ? 2 : 3, py: isMobile ? 2 : 3 }}>
        {/* Quick Actions */}
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 2 : 2.5,
            mb: isMobile ? 2.5 : 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #020617 0%, #0b1120 100%)"
                : "linear-gradient(135deg, #f9fafb 0%, #e5edff 100%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: "space-between",
              mb: 1.5,
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                Quick Actions
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.8rem" }}
              >
                Update status with a single tap
              </Typography>
            </Box>
          </Box>

          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            {getStatusActions().map((action, index) => {
              const IconComponent = action.icon;
              return (
                <Zoom
                  in
                  key={action.status}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <Button
                    variant="contained"
                    startIcon={<IconComponent />}
                    onClick={() => handleStatusChange(action.status)}
                    disabled={loading}
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2.5,
                      py: 1,
                      fontSize: "0.85rem",
                      background: `linear-gradient(135deg, ${getStatusColor(action.status)} 0%, ${getStatusColor(action.status)}dd 70%, #111827 100%)`,
                      boxShadow: "0 10px 22px rgba(15,23,42,0.18)",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 14px 30px rgba(15,23,42,0.22)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                        transform: "none",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {action.label}
                  </Button>
                </Zoom>
              );
            })}
          </Stack>
        </Paper>

        <Grid container spacing={isMobile ? 2 : 3}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} lg={8}>
            {/* Description */}
            {task.description && (
              <Card
                sx={{
                  mb: isMobile ? 2 : 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 14px 40px rgba(15,23,42,0.08)",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.12)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease-out",
                }}
              >
                <CardContent sx={{ p: isMobile ? 2.5 : 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CommentIcon sx={{ color: "#fff", fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, fontSize: "1rem" }}
                    >
                      Description
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.7,
                      color: "text.secondary",
                      fontSize: "0.95rem",
                    }}
                  >
                    {task.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Progress */}
            <Card
              sx={{
                mb: isMobile ? 2 : 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                background:
                  theme.palette.mode === "dark"
                    ? "radial-gradient(circle at top left, #1d4ed8 0, #020617 55%)"
                    : "radial-gradient(circle at top left, #bfdbfe 0, #eff6ff 52%, #ffffff 100%)",
                boxShadow: "0 14px 40px rgba(15,23,42,0.08)",
              }}
            >
              <CardContent sx={{ p: isMobile ? 2.5 : 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2.5,
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TimelineIcon sx={{ color: "#fff", fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 0.3 }}
                    >
                      Progress
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                    >
                      Fine-tune the completion percentage or jump to milestones.
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      sx={{
                        fontWeight: 800,
                        color: getStatusColor(task.status),
                        lineHeight: 1,
                      }}
                    >
                      {task.progress}%
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Complete
                    </Typography>
                  </Box>
                </Box>

                {/* Progress bar */}
                <Box sx={{ position: "relative", mb: 2.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={task.progress}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      backgroundColor: "rgba(15, 23, 42, 0.08)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${getStatusColor(task.status)} 0%, ${getStatusColor(task.status)}dd 60%, #111827 100%)`,
                        boxShadow: "0 4px 16px rgba(15,23,42,0.35)",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: -6,
                      left: `${task.progress}%`,
                      transform: "translateX(-50%)",
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      border: `3px solid ${getStatusColor(task.status)}`,
                      boxShadow: "0 4px 12px rgba(15,23,42,0.25)",
                    }}
                  />
                </Box>

                {/* Milestones */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1.5, fontSize: "0.9rem" }}
                  >
                    Quick progress updates
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {[25, 50, 75, 100].map((milestone) => (
                      <Button
                        key={milestone}
                        variant={
                          task.progress === milestone ? "contained" : "outlined"
                        }
                        size="small"
                        onClick={() => handleProgressUpdate(milestone)}
                        disabled={loading}
                        sx={{
                          minWidth: 56,
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          textTransform: "none",
                          borderColor: getStatusColor(task.status),
                          color:
                            task.progress === milestone
                              ? "#fff"
                              : getStatusColor(task.status),
                          backgroundColor:
                            task.progress === milestone
                              ? getStatusColor(task.status)
                              : "transparent",
                          "&:hover": {
                            backgroundColor:
                              task.progress === milestone
                                ? getStatusColor(task.status)
                                : `${getStatusColor(task.status)}15`,
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
                          },
                          transition: "all 0.18s ease-out",
                        }}
                      >
                        {milestone}%
                      </Button>
                    ))}
                  </Box>
                </Box>

                {/* Custom input */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "stretch" : "center",
                    gap: 1.5,
                  }}
                >
                  <TextField
                    type="number"
                    label="Custom progress"
                    size="small"
                    value={customProgress}
                    onChange={(e) =>
                      setCustomProgress(
                        Math.min(
                          100,
                          Math.max(0, parseInt(e.target.value) || 0)
                        )
                      )
                    }
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleProgressUpdate(customProgress);
                      }
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    sx={{
                      maxWidth: isMobile ? "100%" : 160,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.9)",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleProgressUpdate(customProgress)}
                    disabled={loading || customProgress === task.progress}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      backgroundColor: "primary.main",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
                      },
                      "&:disabled": {
                        backgroundColor: "grey.300",
                        color: "grey.600",
                        transform: "none",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Update
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Comments / Discussion */}
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 14px 40px rgba(15,23,42,0.08)",
              }}
            >
              <CardContent sx={{ p: isMobile ? 2.5 : 3 }}>
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CommentIcon sx={{ color: "#fff", fontSize: 17 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, fontSize: "0.98rem" }}
                      >
                        Discussion
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontSize: "0.72rem" }}
                      >
                        {task.comments?.length || 0} comment
                        {task.comments?.length !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </Box>

                  {notification && (
                    <Fade in>
                      <Paper
                        sx={{
                          px: 1.75,
                          py: 0.8,
                          borderRadius: 999,
                          backgroundColor:
                            notification.severity === "success"
                              ? "success.light"
                              : "error.light",
                          color:
                            notification.severity === "success"
                              ? "success.dark"
                              : "error.dark",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          boxShadow: "0 6px 18px rgba(15,23,42,0.18)",
                        }}
                      >
                        {notification.message}
                      </Paper>
                    </Fade>
                  )}
                </Box>

                {/* Add comment */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "grey.50",
                    transition: "all 0.18s ease-out",
                    "&:focus-within": {
                      backgroundColor: "#fff",
                      borderColor: "primary.main",
                      boxShadow: "0 0 0 3px rgba(59,130,246,0.18)",
                    },
                  }}
                >
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={isMobile ? 1.5 : 1}
                    alignItems={isMobile ? "stretch" : "flex-end"}
                  >
                    {/* Avatar + Input */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        flex: 1,
                        alignItems: "flex-start",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "grey.500",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        U
                      </Avatar>

                      <InputBase
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Add a comment..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleAddComment();
                          }
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          lineHeight: 1.5,
                          px: 1.25,
                          py: 0.75,
                          borderRadius: 2,
                          backgroundColor: "#fff",
                          border: "1px solid rgba(148,163,184,0.6)",
                          transition: "all 0.15s ease-out",
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                          "&:focus-within": {
                            borderColor: "primary.main",
                            boxShadow: "0 0 0 1px rgba(59,130,246,0.4)",
                          },
                          "& textarea": {
                            padding: 0, // remove default textarea padding
                          },
                        }}
                      />
                    </Box>

                    {/* Send button */}
                    <Tooltip title="Post comment">
                      <span style={{ width: isMobile ? "100%" : "auto" }}>
                        <Button
                          onClick={handleAddComment}
                          disabled={!commentText.trim()}
                          variant="contained"
                          fullWidth={isMobile}
                          sx={{
                            minWidth: 48,
                            height: 44,
                            mt: isMobile ? 0.25 : 0,
                            borderRadius: 999,
                            textTransform: "none",
                            fontSize: "0.85rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 8px 22px rgba(37,99,235,0.35)",
                            "&:hover": {
                              boxShadow: "0 14px 30px rgba(37,99,235,0.45)",
                              transform: "translateY(-1px)",
                            },
                            "&:disabled": {
                              boxShadow: "none",
                              transform: "none",
                            },
                            transition: "all 0.18s ease-out",
                          }}
                        >
                          <SendIcon
                            sx={{
                              fontSize: 18,
                              transform: "rotate(-35deg)",
                              mr: isMobile ? 0.5 : 0,
                            }}
                          />
                          {!isMobile && "Send"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Paper>

                {/* Comments list */}
                <Box
                  data-comments-container
                  sx={{
                    maxHeight: 480,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: 6,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "grey.100",
                      borderRadius: 999,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "grey.400",
                      borderRadius: 999,
                    },
                  }}
                >
                  {task.comments && task.comments.length > 0 ? (
                    <Stack spacing={1.5}>
                      {task.comments.map((comment, index) => (
                        <Fade
                          in
                          key={comment._id || index}
                          style={{ transitionDelay: `${index * 40}ms` }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.75,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              backgroundColor: "grey.50",
                              "&:hover": {
                                backgroundColor: "#fff",
                                borderColor: "primary.light",
                                transform: "translateX(1px)",
                              },
                              transition: "all 0.16s ease-out",
                            }}
                          >
                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <Avatar
                                    sx={{
                                      width: 26,
                                      height: 26,
                                      bgcolor: `hsl(${(index * 137.5) % 360}, 70%, 50%)`,
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    {comment.userId?.firstName?.charAt(0)}
                                    {comment.userId?.lastName?.charAt(0)}
                                  </Avatar>
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      {comment.userId?.firstName}{" "}
                                      {comment.userId?.lastName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {formatDate(comment.createdAt)} •{" "}
                                      {new Date(
                                        comment.createdAt
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Per-comment menu (single anchor) */}
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCommentMenuOpen(e);
                                      setCommentModal((prev) => ({
                                        ...prev,
                                        comment,
                                      }));
                                    }}
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      opacity: 0.6,
                                      "&:hover": { opacity: 1 },
                                    }}
                                  >
                                    <MoreVertIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.primary",
                                  lineHeight: 1.5,
                                  whiteSpace: "pre-wrap",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {comment.message}
                              </Typography>
                            </Box>
                          </Paper>
                        </Fade>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                      <CommentIcon
                        sx={{ fontSize: 36, color: "grey.300", mb: 1.5 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        No comments yet
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Start the conversation above.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid item xs={12} lg={4}>
            {/* Task Details */}
            <Card
              sx={{
                mb: isMobile ? 2 : 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #020617 0%, #020617 60%, #111827 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #f9fafb 60%, #eff6ff 100%)",
                boxShadow: "0 14px 40px rgba(15,23,42,0.08)",
              }}
            >
              <CardContent sx={{ p: isMobile ? 2.5 : 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon sx={{ color: "#fff", fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, fontSize: "1rem" }}
                    >
                      Task details
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                    >
                      Key metadata and team members
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={2}>
                  {/* Timeline */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.25,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "grey.50",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <CalendarIcon
                        sx={{ color: "primary.main", fontSize: 20 }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                      >
                        Timeline
                      </Typography>
                    </Box>

                    <Stack spacing={1.5}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                            letterSpacing: 0.3,
                          }}
                        >
                          START DATE
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatDate(task.startDate)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                            letterSpacing: 0.3,
                          }}
                        >
                          DUE DATE
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: task.isOverdue
                              ? "error.main"
                              : "text.primary",
                          }}
                        >
                          {formatDate(task.dueDate)}
                          {task.isOverdue && (
                            <Typography
                              component="span"
                              sx={{
                                ml: 0.5,
                                fontSize: "0.75rem",
                                color: "error.main",
                              }}
                            >
                              ({Math.abs(task.daysRemaining)} days overdue)
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {/* Assigned team */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.25,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "grey.50",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <PersonIcon
                        sx={{ color: "primary.main", fontSize: 20 }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                      >
                        Assigned team ({task.assignedToUsers?.length || 0})
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {task.assignedToUsers?.map((user, index) => (
                        <Zoom
                          in
                          key={user._id}
                          style={{ transitionDelay: `${index * 50}ms` }}
                        >
                          <Chip
                            size="small"
                            label={`${user.firstName} ${user.lastName}`}
                            avatar={
                              <Avatar
                                sx={{
                                  width: 22,
                                  height: 22,
                                  bgcolor: `hsl(${(index * 137.5) % 360}, 70%, 50%)`,
                                  fontSize: "0.7rem",
                                }}
                              >
                                {user.firstName?.charAt(0)}
                                {user.lastName?.charAt(0)}
                              </Avatar>
                            }
                            sx={{
                              borderRadius: 999,
                              fontWeight: 600,
                              backgroundColor: "#fff",
                              border: "1px solid",
                              borderColor: "divider",
                              fontSize: "0.7rem",
                              "&:hover": {
                                backgroundColor: "primary.light",
                                color: "#fff",
                              },
                            }}
                          />
                        </Zoom>
                      ))}
                    </Box>
                  </Paper>

                  {/* Tags */}
                  {task.tags && task.tags.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.25,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "grey.50",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <LabelIcon
                          sx={{ color: "primary.main", fontSize: 20 }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        >
                          Tags
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
                      >
                        {task.tags.map((tag, index) => (
                          <Fade
                            in
                            key={tag}
                            style={{ transitionDelay: `${index * 40}ms` }}
                          >
                            <Chip
                              size="small"
                              label={tag}
                              variant="outlined"
                              sx={{
                                borderRadius: 999,
                                fontWeight: 500,
                                borderColor: "primary.light",
                                color: "primary.main",
                                fontSize: "0.7rem",
                                "&:hover": {
                                  backgroundColor: "primary.light",
                                  color: "#fff",
                                },
                              }}
                            />
                          </Fade>
                        ))}
                      </Box>
                    </Paper>
                  )}

                  {/* Time estimate */}
                  {task.estimatedHours > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.25,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "grey.50",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <AccessTimeIcon
                          sx={{ color: "primary.main", fontSize: 20 }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        >
                          Time estimate
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "primary.main",
                          mb: 0.5,
                        }}
                      >
                        {task.estimatedHours} hours
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                      >
                        Approximate time to complete this task.
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Overdue alert */}
            {task.isOverdue && (
              <Alert
                severity="error"
                icon={<AccessTimeIcon />}
                sx={{
                  mb: isMobile ? 2 : 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "error.light",
                  background:
                    "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
                  boxShadow: "0 10px 28px rgba(248,113,113,0.35)",
                  fontSize: "0.85rem",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.9rem" }}
                >
                  ⚠️ Task overdue
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                  This task was due on {formatDate(task.dueDate)} and is{" "}
                  {Math.abs(task.daysRemaining)} days past due.
                </Typography>
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 1.5 : 2, gap: 1.5 }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size={isMobile ? "medium" : "large"}
          sx={{
            borderRadius: 999,
            px: 2.5,
            fontSize: "0.9rem",
            textTransform: "none",
          }}
        >
          Close
        </Button>
      </DialogActions>

      {/* Task context menu */}
      <Menu
        anchorEl={taskMenuAnchorEl}
        open={Boolean(taskMenuAnchorEl)}
        onClose={handleTaskMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 160,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            onEdit(task);
            handleTaskMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1.5, fontSize: 18 }} />
          Edit task
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            onDelete(task);
            handleTaskMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon sx={{ mr: 1.5, fontSize: 18 }} />
          Delete task
        </MenuItem>
      </Menu>

      {/* Comment actions menu (edit/delete) */}
      <Menu
        anchorEl={commentMenuAnchorEl}
        open={Boolean(commentMenuAnchorEl)}
        onClose={handleCommentMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 150,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (commentModal.comment) {
              handleOpenCommentModal("edit", commentModal.comment);
            }
            handleCommentMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1.5, fontSize: 16 }} />
          Edit comment
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (commentModal.comment) {
              handleOpenCommentModal("delete", commentModal.comment);
            }
            handleCommentMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon sx={{ mr: 1.5, fontSize: 16 }} />
          Delete comment
        </MenuItem>
      </Menu>

      {/* Comment Edit Modal */}
      <Dialog
        open={commentModal.open && commentModal.mode === "edit"}
        onClose={handleCloseCommentModal}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={220}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: "0 32px 64px rgba(15,23,42,0.45)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            background:
              "linear-gradient(135deg, #6366f1 0%, #8b5cf6 40%, #ec4899 100%)",
            color: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                backgroundColor: "rgba(15,23,42,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(255,255,255,0.4)",
              }}
            >
              <EditIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Edit comment
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Update your comment content.
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {commentModal.comment && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <CommentIcon sx={{ fontSize: 16 }} />
                Original comment
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  "{commentModal.comment.message}"
                </Typography>
              </Paper>
            </Box>
          )}

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
              New comment
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={5}
              value={editCommentText}
              onChange={(e) => setEditCommentText(e.target.value)}
              placeholder="Enter your updated comment..."
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  backgroundColor: "grey.50",
                  "& fieldset": { borderWidth: 1.5 },
                  "&:hover fieldset": { borderColor: "primary.main" },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                    borderWidth: 0,
                    boxShadow: "0 0 0 3px rgba(59,130,246,0.18)",
                  },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mt: 0.5, display: "block" }}
            >
              {editCommentText.length} characters
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            backgroundColor: "grey.50",
            borderTop: "1px solid",
            borderColor: "grey.200",
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleCloseCommentModal}
            variant="outlined"
            size="large"
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (editCommentText.trim() && commentModal.comment) {
                handleEditComment(
                  commentModal.comment._id,
                  editCommentText.trim()
                );
              }
            }}
            variant="contained"
            size="large"
            disabled={isSubmitting || !editCommentText.trim()}
            sx={{
              borderRadius: 2,
              px: 3.5,
              textTransform: "none",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              boxShadow: "0 10px 26px rgba(79,70,229,0.45)",
              "&:hover": {
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "grey.300",
                color: "grey.600",
                boxShadow: "none",
                transform: "none",
              },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "2px solid transparent",
                    borderTopColor: "currentColor",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Updating...
              </Box>
            ) : (
              "Update comment"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment Delete Modal (MUI version) */}
      <Dialog
        open={commentModal.open && commentModal.mode === "delete"}
        onClose={handleCloseCommentModal}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={220}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 56px rgba(15,23,42,0.55)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "error.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DeleteIcon sx={{ color: "error.main" }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Delete comment
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.3 }}
            >
              Are you sure you want to delete this comment?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {commentModal.comment && (
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontStyle: "italic", color: "text.secondary" }}
              >
                "{commentModal.comment.message}"
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCloseCommentModal}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (commentModal.comment) {
                handleDeleteComment(commentModal.comment._id);
              }
            }}
            disabled={isSubmitting}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            {isSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TaskDetail;
